import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, PageBody, EmptyState } from "@/components/ui-bits";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Folders } from "lucide-react";
import { toast } from "sonner";

export default function DeviceGroups() {
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });

  const load = async () => {
    const { data } = await api.get("/groups");
    setGroups(data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    await api.post("/groups", form);
    toast.success("Group created");
    setOpen(false);
    setForm({ name: "", description: "" });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this group? Devices stay, just ungrouped.")) return;
    await api.delete(`/groups/${id}`);
    toast.success("Group removed");
    load();
  };

  return (
    <div data-testid="groups-page">
      <PageHeader
        eyebrow="// ORGANISATION"
        title="Device groups"
        description="Hierarchical grouping for thousands of sensors. Filter telemetry, alerts, and exports by group."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="open-add-group-btn" className="inline-flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#001E70]">
                <Plus className="w-4 h-4" /> New group
              </button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-neutral-900">
              <DialogHeader><DialogTitle className="font-display tracking-tight">New group</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="label-eyebrow text-neutral-500">Name</Label>
                  <Input data-testid="group-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <Label className="label-eyebrow text-neutral-500">Description</Label>
                  <Input data-testid="group-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm hover:bg-neutral-100">Cancel</button>
                <button onClick={create} data-testid="confirm-add-group-btn" className="px-4 py-2 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#001E70]">Create</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <PageBody>
        {groups.length === 0 ? (
          <EmptyState title="No groups yet" body="Group your devices by location, line, or use-case." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
            {groups.map(g => (
              <div key={g.group_id} className="bg-white p-5 flex flex-col" data-testid={`group-card-${g.group_id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Folders className="w-4 h-4 text-[#002FA7]" />
                    <div className="font-display font-semibold">{g.name}</div>
                  </div>
                  <button onClick={() => remove(g.group_id)} data-testid={`delete-group-${g.group_id}`} className="p-1 text-[#FF3B30] hover:bg-neutral-100"><Trash2 className="w-4 h-4" /></button>
                </div>
                {g.description && <p className="text-sm text-neutral-600 mt-2">{g.description}</p>}
                <div className="mt-4 label-eyebrow text-neutral-500">Devices</div>
                <div className="font-mono text-2xl font-bold mt-1">{g.device_count}</div>
              </div>
            ))}
          </div>
        )}
      </PageBody>
    </div>
  );
}
